import React from "react";
import { useDangerConfirm } from "../../../shared/context/DangerConfirmContext.jsx";
import { Card } from "../../../shared/components/Card.jsx";
import { Badge } from "../../../shared/components/Badge.jsx";
import { Button } from "../../../shared/components/Button.jsx";

export function CustomerCard({
  customer,
  onEdit,
  onDelete,
  onManageAddresses,
  onConfigurePrices,
  onViewInvoices,
  onTallyPayment,
  onViewLedger // (customer, tab) => void — opens the ledger sheet on a tab
}) {
  const { confirmDanger } = useDangerConfirm();
  const balance = Number(customer.companyDue ?? customer.openingBalance ?? 0);
  const customRatesCount = Object.keys(customer.customPrices || {}).length;
  const partyType = customer.partyType || "customer";
  const isVendor = partyType === "vendor";
  const isBoth = partyType === "both";
  const hasBank = !!(customer.bankDetails?.bankName || customer.bankDetails?.accountNumber || customer.bankDetails?.upiId);

  const handleDelete = async (e) => {
    e.stopPropagation();
    const typeText = isVendor ? "vendor" : isBoth ? "party" : "customer";
    if (await confirmDanger({ title: `Delete ${typeText}?`, message: `Are you sure you want to delete ${typeText} "${customer.name}"?` })) {
      onDelete(customer.id);
    }
  };

  return (
    <Card style={{ marginBottom: "0.75rem" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "0.5rem" }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap" }}>
            <span style={{ fontWeight: "700", fontSize: "1rem", color: "var(--text-main)" }}>
              {customer.name}
            </span>

            {/* Party Type Indicator */}
            {isVendor ? (
              <Badge style={{ background: "rgba(99, 102, 241, 0.18)", color: "#818cf8", border: "1px solid rgba(99, 102, 241, 0.4)", fontSize: "0.68rem" }}>
                🏭 Vendor
              </Badge>
            ) : isBoth ? (
              <Badge style={{ background: "rgba(16, 185, 129, 0.18)", color: "#34d399", border: "1px solid rgba(16, 185, 129, 0.4)", fontSize: "0.68rem" }}>
                🔄 Both
              </Badge>
            ) : (
              <Badge style={{ background: "rgba(56, 189, 248, 0.15)", color: "#38bdf8", border: "1px solid rgba(56, 189, 248, 0.4)", fontSize: "0.68rem" }}>
                🛒 Customer
              </Badge>
            )}

            {customer.gstin ? (
              <Badge variant="info" style={{ fontSize: "0.68rem" }}>
                GST: {customer.gstin}
              </Badge>
            ) : (
              <Badge variant="warning" style={{ fontSize: "0.68rem" }}>
                Unregistered
              </Badge>
            )}

            {customRatesCount > 0 && (
              <Badge variant="primary" style={{ fontSize: "0.68rem" }}>
                🏷️ {customRatesCount} custom {customRatesCount === 1 ? "rate" : "rates"}
              </Badge>
            )}
          </div>

          {customer.mobile && (
            <div style={{ marginTop: "4px" }}>
              <a
                href={`tel:${customer.mobile}`}
                style={{
                  fontSize: "0.85rem",
                  color: "#60a5fa",
                  textDecoration: "none",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "4px"
                }}
              >
                📞 {customer.mobile}
              </a>
            </div>
          )}

          <div style={{ fontSize: "0.75rem", color: "var(--text-dim)", marginTop: "4px" }}>
            📍 {customer.city ? `${customer.city}, ` : ""}{customer.state || "Tamil Nadu"} ({customer.stateCode || "33"})
          </div>

          {/* Vendor Bank Details Card */}
          {hasBank && (
            <div style={{
              marginTop: "6px",
              padding: "4px 8px",
              borderRadius: "4px",
              background: "rgba(99, 102, 241, 0.08)",
              border: "1px dashed rgba(99, 102, 241, 0.3)",
              fontSize: "0.75rem",
              color: "var(--text-secondary, #cbd5e1)",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              flexWrap: "wrap"
            }}>
              <span>🏦 {customer.bankDetails.bankName || "Bank"}:</span>
              {customer.bankDetails.accountNumber && (
                <span>A/C: <strong style={{ color: "#fff" }}>{customer.bankDetails.accountNumber}</strong></span>
              )}
              {customer.bankDetails.ifscCode && (
                <span>IFSC: <strong style={{ color: "#fff" }}>{customer.bankDetails.ifscCode}</strong></span>
              )}
              {customer.bankDetails.upiId && (
                <span>UPI: <strong style={{ color: "#60a5fa" }}>{customer.bankDetails.upiId}</strong></span>
              )}
            </div>
          )}
        </div>

        <div style={{ textAlign: "right", display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
          <div style={{ fontSize: "0.7rem", color: "var(--text-dim)", textTransform: "uppercase" }}>
            {isVendor ? "To Pay" : isBoth ? "Balance" : "To Receive"}
          </div>
          <div style={{
            fontSize: "1rem",
            fontWeight: "700",
            color: isVendor ? (balance > 0 ? "#f87171" : "var(--success)") : (balance > 0 ? "var(--warning)" : "var(--success)")
          }}>
            ₹{balance.toLocaleString("en-IN")}
          </div>
        </div>
      </div>

      {/* Action Row */}
      <div style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        flexWrap: "wrap",
        gap: "0.5rem",
        marginTop: "0.85rem",
        paddingTop: "0.65rem",
        borderTop: "1px solid var(--border-subtle)"
      }}>
        <div style={{ display: "flex", gap: "0.35rem", flexWrap: "wrap" }}>
          {onViewLedger && (
            <>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => onViewLedger(customer, "ledger")}
                style={{ fontSize: "0.72rem", padding: "0.25rem 0.55rem", minHeight: "28px" }}
              >
                📒 Ledger
              </Button>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => onViewLedger(customer, "payments")}
                style={{ fontSize: "0.72rem", padding: "0.25rem 0.55rem", minHeight: "28px" }}
              >
                💳 Payments
              </Button>
            </>
          )}
          <Button
            size="sm"
            variant="secondary"
            onClick={() => onViewInvoices(customer)}
            style={{ fontSize: "0.72rem", padding: "0.25rem 0.55rem", minHeight: "28px" }}
          >
            🧾 Invoices
          </Button>
          {balance > 0 && onTallyPayment && (
            <Button
              size="sm"
              variant="primary"
              onClick={() => onTallyPayment(customer)}
              style={{ fontSize: "0.72rem", padding: "0.25rem 0.55rem", minHeight: "28px" }}
            >
              💰 Settle
            </Button>
          )}
          <Button
            size="sm"
            variant="secondary"
            onClick={() => onConfigurePrices(customer)}
            style={{ fontSize: "0.72rem", padding: "0.25rem 0.55rem", minHeight: "28px" }}
          >
            🏷️ Custom Rates
          </Button>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => onManageAddresses(customer)}
            style={{ fontSize: "0.72rem", padding: "0.25rem 0.55rem", minHeight: "28px" }}
          >
            📍 Addresses
          </Button>
        </div>

        <div style={{ display: "flex", gap: "0.35rem" }}>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => onEdit(customer)}
            style={{ fontSize: "0.75rem", padding: "0.25rem 0.5rem", minHeight: "28px" }}
            aria-label="Edit"
          >
            ✏️
          </Button>
          <Button
            size="sm"
            variant="danger"
            onClick={handleDelete}
            style={{ fontSize: "0.75rem", padding: "0.25rem 0.5rem", minHeight: "28px" }}
            aria-label="Delete"
          >
            🗑️
          </Button>
        </div>
      </div>
    </Card>
  );
}

export default CustomerCard;
